/**
 * Chems Carte Diplômées — Section JS
 * ===================================
 * Encapsulated class, vanilla JS, no jQuery.
 * Lazy init via IntersectionObserver.
 * Compatible with shopify:section:load for the theme editor.
 */
(function () {
  'use strict';

  class ChemsCarteDiplomees {
    constructor(sectionEl) {
      this.section = sectionEl;
      this.sectionId = sectionEl.dataset.sectionId;
      this.map = null;
      this.allFeatures = [];
      this.markers = [];
      this.markerLayer = null;
      this.clusterGroup = null;
      this.initialized = false;

      // Settings from data attributes
      this.settings = {
        dataUrl: sectionEl.dataset.dataUrl || '',
        zoom: parseInt(sectionEl.dataset.zoom, 10) || 4,
        lat: parseFloat(sectionEl.dataset.lat) || 46,
        lng: parseFloat(sectionEl.dataset.lng) || 5,
        clustering: sectionEl.dataset.clustering !== 'false',
        search: sectionEl.dataset.search !== 'false',
        filter: sectionEl.dataset.filter !== 'false',
      };

      // DOM refs
      this.mapContainer = sectionEl.querySelector('[data-map]');
      this.searchInput = sectionEl.querySelector('[data-search-input]');
      this.searchClear = sectionEl.querySelector('[data-search-clear]');
      this.filterSelect = sectionEl.querySelector('[data-filter-select]');
      this.counterEl = sectionEl.querySelector('[data-counter-text]');
      this.counterTemplate = sectionEl.dataset.counterTemplate || '';
      this.loadingEl = sectionEl.querySelector('[data-loading]');
      this.noResultsEl = sectionEl.querySelector('[data-no-results]');

      this._setupLazyInit();
    }

    /** IntersectionObserver: init when section enters viewport */
    _setupLazyInit() {
      if (typeof IntersectionObserver === 'undefined') {
        this._init();
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && !this.initialized) {
            observer.disconnect();
            this._init();
          }
        },
        { rootMargin: '200px' }
      );
      observer.observe(this.section);
    }

    /** Main initialization */
    async _init() {
      if (this.initialized) return;
      this.initialized = true;

      // Wait for Leaflet to be available
      await this._waitForLeaflet();

      // Create map
      this._createMap();

      // Load data
      try {
        const data = await this._fetchData();
        this.allFeatures = data.features || [];
        this._populateCountryFilter(data);
        this._placeMarkers(this.allFeatures);
        this._updateCounter(this.allFeatures, data.meta);
        this._bindEvents();
        this._hideLoading();
      } catch (err) {
        console.error('[ChemsCarteDiplomees] Error loading data:', err);
        this._hideLoading();
      }

      // Fade in elements
      this.section.querySelectorAll('.chems-carte__fade-in').forEach((el) => {
        el.classList.add('chems-carte__fade-in--visible');
      });
    }

    /** Wait until L (Leaflet) is defined */
    _waitForLeaflet() {
      return new Promise((resolve) => {
        if (typeof L !== 'undefined') return resolve();
        const interval = setInterval(() => {
          if (typeof L !== 'undefined') {
            clearInterval(interval);
            resolve();
          }
        }, 100);
        // Timeout after 10s
        setTimeout(() => {
          clearInterval(interval);
          resolve();
        }, 10000);
      });
    }

    /** Create Leaflet map */
    _createMap() {
      if (!this.mapContainer || typeof L === 'undefined') return;

      this.map = L.map(this.mapContainer, {
        center: [this.settings.lat, this.settings.lng],
        zoom: this.settings.zoom,
        scrollWheelZoom: false,
        zoomControl: true,
      });

      // Use OSM FR for French labels
      L.tileLayer('https://{s}.tile.openstreetmap.fr/osmfr/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        maxZoom: 18,
      }).addTo(this.map);

      // Enable scroll zoom on focus
      this.map.on('click', () => {
        this.map.scrollWheelZoom.enable();
      });
    }

    /** Fetch GeoJSON data */
    async _fetchData() {
      const response = await fetch(this.settings.dataUrl);
      if (!response.ok) throw new Error('Failed to fetch data: ' + response.status);
      return response.json();
    }

    /** Create custom marker icon */
    _createMarkerIcon() {
      if (typeof L === 'undefined') return null;
      
      const iconUrl = this.section.dataset.markerIcon;
      const iconHtml = `<img src="${iconUrl}" class="chems-carte__marker-img" alt="Chems" />`;
      
      return L.divIcon({
        className: 'chems-carte__marker-icon',
        html: `<span class="chems-carte__marker-icon-inner">${iconHtml}</span>`,
        iconSize: [36, 36],
        iconAnchor: [18, 36],
        popupAnchor: [0, -36],
      });
    }

    /** Place markers on the map */
    _placeMarkers(features) {
      if (!this.map || typeof L === 'undefined') return;

      // Clear existing markers
      if (this.clusterGroup) {
        this.map.removeLayer(this.clusterGroup);
      }
      if (this.markerLayer) {
        this.map.removeLayer(this.markerLayer);
      }
      this.markers = [];

      const icon = this._createMarkerIcon();
      const markersArray = [];

      features.forEach((feature) => {
        const coords = feature.geometry.coordinates;
        const props = feature.properties;
        const marker = L.marker([coords[1], coords[0]], { icon: icon });

        let diplomesHtml = '';
        const diplomes = [props.diplome1, props.diplome2, props.diplome3].filter(d => d && d.trim() !== '');
        if (diplomes.length > 0) {
          diplomesHtml = '<div class="chems-carte__popup-diplomes">';
          diplomes.forEach(d => {
            diplomesHtml += '<span class="chems-carte__popup-diplome">' + this._escapeHtml(d) + '</span>';
          });
          diplomesHtml += '</div>';
        }

        const popupHtml =
          '<div class="chems-carte__popup">' +
          '<p class="chems-carte__popup-name">' +
          this._escapeHtml(props.prenom) + ' ' + this._escapeHtml(props.nom) +
          '</p>' +
          '<p class="chems-carte__popup-location">' +
          '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>' +
          this._escapeHtml(props.ville) + ', ' + this._escapeHtml(props.pays) +
          '</p>' +
          diplomesHtml +
          '</div>';

        marker.bindPopup(popupHtml, { maxWidth: 280, minWidth: 200, className: 'chems-carte__custom-popup' });
        marker._chemsData = props;
        markersArray.push(marker);
        this.markers.push(marker);
      });

      // Use clustering if enabled and available
      if (this.settings.clustering && typeof L.markerClusterGroup === 'function') {
        this.clusterGroup = L.markerClusterGroup({
          maxClusterRadius: 50,
          spiderfyOnMaxZoom: true,
          showCoverageOnHover: false,
          zoomToBoundsOnClick: true,
          disableClusteringAtZoom: 12,
        });
        markersArray.forEach((m) => this.clusterGroup.addLayer(m));
        this.map.addLayer(this.clusterGroup);
      } else {
        this.markerLayer = L.layerGroup(markersArray);
        this.markerLayer.addTo(this.map);
      }
    }

    /** Populate country filter dropdown */
    _populateCountryFilter(data) {
      if (!this.filterSelect || !data.features) return;
      const countries = new Set();
      data.features.forEach((f) => {
        if (f.properties.pays) countries.add(f.properties.pays);
      });

      const sorted = [...countries].sort((a, b) => a.localeCompare(b, 'fr'));
      sorted.forEach((country) => {
        const option = document.createElement('option');
        option.value = country;
        option.textContent = country;
        this.filterSelect.appendChild(option);
      });
    }

    /** Bind search and filter events */
    _bindEvents() {
      if (this.searchInput) {
        this.searchInput.addEventListener('input', () => this._applyFilters());
      }
      if (this.searchClear) {
        this.searchClear.addEventListener('click', () => {
          if (this.searchInput) this.searchInput.value = '';
          if (this.searchClear) this.searchClear.classList.remove('chems-carte__search-clear--visible');
          this._applyFilters();
        });
      }
      if (this.filterSelect) {
        this.filterSelect.addEventListener('change', () => this._applyFilters());
      }
    }

    /** Apply search + country filter */
    _applyFilters() {
      const query = this.searchInput ? this.searchInput.value.trim().toLowerCase() : '';
      const country = this.filterSelect ? this.filterSelect.value : '';

      // Toggle clear button
      if (this.searchClear) {
        this.searchClear.classList.toggle('chems-carte__search-clear--visible', query.length > 0);
      }

      const filtered = this.allFeatures.filter((f) => {
        const props = f.properties;

        // Country filter
        if (country && props.pays !== country) return false;

        // Search filter (name or city)
        if (query) {
          const searchable = [props.prenom, props.nom, props.ville]
            .join(' ')
            .toLowerCase();
          if (!searchable.includes(query)) return false;
        }

        return true;
      });

      this._placeMarkers(filtered);
      this._updateCounter(filtered, { countries: this._countCountries(filtered) });

      if (query || country) {
        this._zoomToFeatures(filtered);
      } else {
        if (this.map && typeof L !== 'undefined') {
          this.map.setView([this.settings.lat, this.settings.lng], this.settings.zoom);
        }
      }

      // Show/hide no results
      if (this.noResultsEl) {
        this.noResultsEl.classList.toggle(
          'chems-carte__no-results--visible',
          filtered.length === 0 && (query || country)
        );
      }
    }

    /** Zoom map to bounding box of features */
    _zoomToFeatures(features) {
      if (!this.map || typeof L === 'undefined' || features.length === 0) return;
      
      const bounds = L.latLngBounds();
      features.forEach((feature) => {
        const coords = feature.geometry.coordinates;
        bounds.extend([coords[1], coords[0]]);
      });

      if (bounds.isValid()) {
        this.map.fitBounds(bounds, {
          padding: [40, 40],
          maxZoom: 10
        });
      }
    }

    /** Count unique countries in a feature set */
    _countCountries(features) {
      const set = new Set();
      features.forEach((f) => set.add(f.properties.pays));
      return set.size;
    }

    /** Update the dynamic counter */
    _updateCounter(features, meta) {
      if (!this.counterEl || !this.counterTemplate) return;
      const total = features.length;
      const countries = meta ? (meta.countries || this._countCountries(features)) : this._countCountries(features);
      const text = this.counterTemplate
        .replace('{total}', total)
        .replace('{pays}', countries);
      this.counterEl.textContent = text;
    }

    /** Hide loading overlay */
    _hideLoading() {
      if (this.loadingEl) {
        this.loadingEl.classList.add('chems-carte__loading--hidden');
        setTimeout(() => {
          this.loadingEl.style.display = 'none';
        }, 400);
      }
    }

    /** Escape HTML to prevent XSS */
    _escapeHtml(str) {
      if (!str) return '';
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }

    /** Destroy instance (for re-init in theme editor) */
    destroy() {
      if (this.map) {
        this.map.remove();
        this.map = null;
      }
      this.initialized = false;
      this.markers = [];
      this.allFeatures = [];
    }
  }

  // ── Section registry ─────────────────────────────────────────────────
  const instances = new Map();

  function initSection(container) {
    const el = container.querySelector('[data-chems-carte-diplomees]') || container;
    if (!el.dataset.chemsCarteDiplomees && el.dataset.chemsCarteDiplomees !== '') return;
    if (instances.has(el.dataset.sectionId)) {
      instances.get(el.dataset.sectionId).destroy();
    }
    const instance = new ChemsCarteDiplomees(el);
    instances.set(el.dataset.sectionId, instance);
  }

  // Auto-init all sections on DOM ready
  function onReady() {
    document.querySelectorAll('[data-chems-carte-diplomees]').forEach((el) => {
      const instance = new ChemsCarteDiplomees(el);
      instances.set(el.dataset.sectionId, instance);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }

  // Theme editor support
  document.addEventListener('shopify:section:load', (e) => {
    initSection(e.target);
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const sectionEl = e.target.querySelector('[data-chems-carte-diplomees]');
    if (sectionEl && instances.has(sectionEl.dataset.sectionId)) {
      instances.get(sectionEl.dataset.sectionId).destroy();
      instances.delete(sectionEl.dataset.sectionId);
    }
  });
})();
